import type { RefObject } from 'react'
import {
  LOCALE_LABELS,
  SUPPORTED_LOCALES,
  type Locale,
  type Translations
} from '../../i18n/translations'
import settingsDarkIcon from '../../assets/icons/dark/settings.png'
import settingsLightIcon from '../../assets/icons/light/settings.png'

interface SettingsMenuProps {
  t: Translations
  locale: Locale
  setLocale: (locale: Locale) => void
  accountName: string
  authStatus: 'disabled' | 'loading' | 'signedOut' | 'signedIn'
  isDarkMode: boolean
  setIsDarkMode: (updater: (value: boolean) => boolean) => void
  canOpenAlbumViews: boolean
  setMiddleFrameTab: (tab: 'album' | 'history' | 'configuration') => void
  handleBackToSelector: () => void
  canOpenThemeReview: boolean
  onOpenThemeReview: () => void
  setIsHelpOpen: (value: boolean) => void
  setIsSettingsOpen: (value: boolean | ((value: boolean) => boolean)) => void
  onSignOut: () => void
  onOpenHome: () => void
  settingsMenuRef: RefObject<HTMLDivElement>
  isSettingsOpen: boolean
}

export function SettingsMenu({
  t,
  locale,
  setLocale,
  accountName,
  authStatus,
  isDarkMode,
  setIsDarkMode,
  canOpenAlbumViews,
  setMiddleFrameTab,
  handleBackToSelector,
  canOpenThemeReview,
  onOpenThemeReview,
  setIsHelpOpen,
  setIsSettingsOpen,
  onSignOut,
  onOpenHome,
  settingsMenuRef,
  isSettingsOpen
}: SettingsMenuProps): JSX.Element {
  const settingsIcon = isDarkMode ? settingsDarkIcon : settingsLightIcon

  return (
    <div className="settings-fab" ref={settingsMenuRef}>
      <button
        className="settings-fab-button"
        type="button"
        aria-label={t.settings}
        title={t.settings}
        onClick={() => setIsSettingsOpen((value) => !value)}
      >
        <img src={settingsIcon} alt="" aria-hidden="true" className="settings-fab-icon" />
      </button>

      {isSettingsOpen && (
        <section className="settings-menu" role="dialog" aria-label={t.settings}>
          <div className="settings-group">
            <p className="settings-label">{t.usernameLabel}</p>
            <p className="settings-value">{accountName}</p>
            {authStatus === 'signedIn' && (
              <button
                type="button"
                className="auth-link settings-link"
                onClick={() => {
                  onSignOut()
                  setIsSettingsOpen(false)
                }}
              >
                {t.authSignOut}
              </button>
            )}
          </div>

          <div className="settings-group">
            <label className="settings-label" htmlFor="settings-language-select">
              {t.language}
            </label>
            <select
              id="settings-language-select"
              className="settings-select"
              value={locale}
              onChange={(event) => setLocale(event.target.value as Locale)}
            >
              {SUPPORTED_LOCALES.map((option) => (
                <option key={option} value={option}>
                  {LOCALE_LABELS[option]}
                </option>
              ))}
            </select>
          </div>

          <div className="settings-group">
            <p className="settings-label">{t.themeMode}</p>
            <button
              className="ghost-button settings-action"
              type="button"
              onClick={() => setIsDarkMode((value) => !value)}
            >
              {isDarkMode ? t.lightMode : t.darkMode}
            </button>
          </div>

          <div className="settings-group">
            <p className="settings-label">{t.quickLinks}</p>
            <button
              className="ghost-button settings-action"
              type="button"
              onClick={() => {
                onOpenHome()
                setIsSettingsOpen(false)
              }}
            >
              {t.quickLinkHome}
            </button>
            <button
              className="ghost-button settings-action"
              type="button"
              disabled={!canOpenAlbumViews}
              onClick={() => {
                setMiddleFrameTab('history')
                setIsSettingsOpen(false)
              }}
            >
              {t.quickLinkInvestments}
            </button>
            <button
              className="ghost-button settings-action"
              type="button"
              disabled={!canOpenAlbumViews}
              onClick={() => {
                handleBackToSelector()
                setIsSettingsOpen(false)
              }}
            >
              {t.quickLinkAlbumSelection}
            </button>
            {canOpenThemeReview && (
              <button
                className="ghost-button settings-action"
                type="button"
                onClick={() => {
                  onOpenThemeReview()
                  setIsSettingsOpen(false)
                }}
              >
                {t.quickLinkThemeReview}
              </button>
            )}
            <button
              className="ghost-button settings-action"
              type="button"
              onClick={() => {
                setIsHelpOpen(true)
                setIsSettingsOpen(false)
              }}
            >
              {t.quickLinkHelp}
            </button>
            <button
              className="ghost-button settings-action"
              type="button"
              disabled={authStatus !== 'signedIn'}
              onClick={() => {
                onSignOut()
                setIsSettingsOpen(false)
              }}
            >
              {t.authSignOut}
            </button>
          </div>
        </section>
      )}
    </div>
  )
}
